import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, IconButton, TextField, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Save as SaveIcon, Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SHADOWS, SAP_SPACING } from '../theme/sapDesignSystem';
import { toast } from 'sonner';
import { handleApiError } from '../api/errorHandling';
import { createMailSetting, deleteMailSetting, fetchMailSettings, updateMailSetting, type MailSettingItem } from '../api/features/mailSetting/mailSettingApi';

export default function MailSettingsPanel({ clientCode, jobDetailId, clientId, allowEdit = true }: { clientCode: string; jobDetailId: string; clientId: number; allowEdit?: boolean; }) {
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState<MailSettingItem | null>(null);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<string>('');
  const [addOpen, setAddOpen] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const parsedJobDetailId = useMemo(() => Number(jobDetailId), [jobDetailId]);

  const load = async () => {
    setLoading(true);
    try {
      const list = await fetchMailSettings({ clientCode, jobDetailId: parsedJobDetailId });
      const first = Array.isArray(list) && list.length > 0 ? list[0] : null;
      setItem(first);
      setValue(first ? String(first.mailInterval) : '');
    } catch (e) {
      handleApiError(e, 'Failed to load mail setting');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientCode && jobDetailId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientCode, jobDetailId]);

  const startEdit = () => setEditing(true);
  const cancelEdit = () => { setEditing(false); setValue(item ? String(item.mailInterval) : ''); };

  const saveEdit = async () => {
    if (!item) return;
    try {
      const payload = {
        mailInterval: value,
        jobDetailId: parsedJobDetailId,
        clientId: clientId,
        id: item.id as number,
      };
      const res = await updateMailSetting({ clientCode, payload });
      if (res?.success) {
        toast.success('Mail setting updated');
        await load();
        setEditing(false);
        setSaveConfirmOpen(false);
      }
    } catch (e) {
      handleApiError(e, 'Update failed');
    }
  };

  const openSaveConfirm = () => setSaveConfirmOpen(true);
  const confirmSaveEdit = () => saveEdit();

  const openDeleteConfirm = () => setDeleteConfirmOpen(true);
  const confirmDelete = async () => {
    if (!item?.id) return;
    setDeleting(true);
    try {
      await deleteMailSetting({ clientCode, id: item.id });
      toast.success('Mail setting deleted');
      setDeleteConfirmOpen(false);
      setEditing(false);
      await load();
    } catch (e) {
      handleApiError(e, 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const openAdd = () => { setValue(''); setAddOpen(true); };
  const closeAdd = () => setAddOpen(false);

  const confirmAdd = async () => {
    if (!value) { toast.error('Please enter a value'); return; }
    try {
      const payload = {
        mailInterval: value,
        jobDetailId: parsedJobDetailId,
        clientId: clientId,
      };
      const res = await createMailSetting({ clientCode, payload });
      if (res?.success) {
        toast.success('Mail setting created');
        await load();
        closeAdd();
      }
    } catch (e) {
      handleApiError(e, 'Create failed');
    }
  };

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Box sx={{ ml: 'auto' }}>
            {allowEdit && !item && (
              <Tooltip title="Create Mail Setting">
                <span>
                  <IconButton 
                    onClick={openAdd} 
                    disabled={loading} 
                    sx={{ 
                      color: SAP_COLORS.sapBlue,
                      backgroundColor: SAP_COLORS.sapLightBlue,
                      borderRadius: SAP_SPACING.component.borderRadius,
                      boxShadow: SAP_SHADOWS.sm,
                      '&:hover': {
                        backgroundColor: SAP_COLORS.sapBlue,
                        color: SAP_COLORS.sapWhite,
                        transform: 'translateY(-2px)',
                        boxShadow: SAP_SHADOWS.md,
                      },
                      '&:disabled': {
                        backgroundColor: SAP_COLORS.sapGray2,
                        color: SAP_COLORS.sapGray4,
                        boxShadow: 'none',
                        transform: 'none',
                      },
                      transition: 'all 0.2s ease',
                    }} 
                    size="large"
                  >
                    <AddIcon sx={{ fontSize: 28 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box sx={{ border: `1px solid ${SAP_COLORS.sapGray2}`, borderRadius: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr auto' }, alignItems: 'center', px: 2, py: 1.5, backgroundColor: SAP_COLORS.sapPaleBlue, borderBottom: `1px solid ${SAP_COLORS.sapGray2}` }}>
            <Typography sx={{ color: SAP_COLORS.sapGray6, fontWeight: SAP_TYPOGRAPHY.fontWeight.medium }}>Mail Interval (minutes)</Typography>
            <span />
          </Box>

          {item ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr auto' }, alignItems: 'center', px: 2, py: 1.5, '&:hover': { backgroundColor: SAP_COLORS.sapGray1 } }}>
              {allowEdit && editing ? (
                <TextField size="small" fullWidth value={value} onChange={(e) => setValue(e.target.value)} type="number" inputProps={{ min: 0 }} />
              ) : (
                <Typography sx={{ wordBreak: 'break-word' }}>{item.mailInterval}</Typography>
              )}
              <Box display="flex" gap={1} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                {allowEdit && (editing ? (
                  <>
                    <Tooltip title="Save">
                      <IconButton onClick={openSaveConfirm} sx={{ color: SAP_COLORS.sapGreen }}>
                        <SaveIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton onClick={cancelEdit} sx={{ color: SAP_COLORS.sapGray5 }}>
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Tooltip title="Edit">
                      <IconButton onClick={startEdit} sx={{ color: SAP_COLORS.sapBlue }}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={openDeleteConfirm} sx={{ color: SAP_COLORS.sapRed || '#d32f2f' }}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                ))}
              </Box>
            </Box>
          ) : (
            <Box sx={{ px: 2, py: 3 }}>
              <Typography color="text.secondary">
                {allowEdit ? 'No mail setting found. Click + to create.' : 'No mail setting found.'}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>

      <Dialog 
        open={addOpen} 
        onClose={closeAdd} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: SAP_SHADOWS.card,
            backgroundColor: SAP_COLORS.sapWhite,
            overflow: 'hidden',
          }
        }}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.32)' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1, px: 3, py: 2.5, borderBottom: `1px solid ${SAP_COLORS.sapGray2}`, backgroundColor: SAP_COLORS.sapWhite }}>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}>Create Mail Setting</Typography>
          <IconButton onClick={closeAdd} sx={{ color: SAP_COLORS.sapGray6 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 3, pb: 2, overflow: 'visible' }}>
          <Box>
            <Typography 
              variant="body2" 
              sx={{
                fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                fontSize: '14px',
                color: SAP_COLORS.sapGray7,
                mb: 1,
              }}
            >
              Mail Interval (minutes)
            </Typography>
            <TextField 
              placeholder="Enter minutes" 
              size="small" 
              fullWidth 
              type="number" 
              inputProps={{ min: 0 }} 
              value={value} 
              onChange={(e) => setValue(e.target.value)} 
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 44,
                  borderRadius: 1,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: SAP_COLORS.sapGray3,
                    borderWidth: 1,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: SAP_COLORS.sapGray4,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: SAP_COLORS.sapBlue,
                    borderWidth: 2,
                  },
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${SAP_COLORS.sapGray2}`, backgroundColor: SAP_COLORS.sapGray1 }}>
          <Button onClick={closeAdd} variant="text">Cancel</Button>
          <Button onClick={confirmAdd} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Save (Edit) */}
      <Dialog open={saveConfirmOpen} onClose={() => setSaveConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}>Save mail setting?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you want to save the changes to the mail interval (minutes)?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSaveConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmSaveEdit} sx={{ backgroundColor: SAP_COLORS.sapBlue }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete */}
      <Dialog open={deleteConfirmOpen} onClose={() => !deleting && setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}>Delete mail setting?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this mail setting? The mail interval will be removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

 